/**
 * A split-paid order in a list: ONE row with the whole total and a "Split · N payments"
 * badge; opened, every payment (tender, amount, tip, fee, card, time, status, refunded).
 * `split` / `payments` come from the server (order-service GET /api/orders, backend
 * GET /api/merchant/orders) - nothing is computed here.
 */
import React from 'react';
import { SplitSquareHorizontal } from 'lucide-react';
import { formatTime } from '@/lib/restaurantTime';

const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export function SplitBadge({ split, dark = false }) {
  if (!split) return null;
  return (
    <span title={split.text} data-testid="split-badge"
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${dark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-800'}`}>
      <SplitSquareHorizontal className="w-3 h-3" />{split.badge}
    </span>
  );
}

/** timezone: the restaurant's (restaurants.timezone) - a part's time is on its clock, never the browser's. */
export function PaymentParts({ payments, timezone, dark = false }) {
  if (!payments || payments.length === 0) return null;
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  return (
    <div className="space-y-1.5" data-testid="split-parts">
      {payments.map((p, i) => (
        <div key={`${p.kind}-${i}`} data-testid={`split-part-row-${i}`}
          className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
          <div className="min-w-0">
            <p className={`font-medium ${p.status === 'voided' ? 'line-through opacity-60' : ''} ${dark ? 'text-white' : 'text-slate-900'}`}>{p.label}</p>
            <p className={`text-[11px] ${muted}`}>
              {p.at ? formatTime(p.at, timezone) : ''}
              {p.tip_cents ? ` · tip ${usd(p.tip_cents)}` : ''}
              {p.fee_cents ? ` · fee ${usd(p.fee_cents)}` : ''}
              {p.status === 'voided' ? ' · voided' : ''}
              {p.refunded_cents ? ` · refunded ${usd(p.refunded_cents)}` : ''}
            </p>
          </div>
          <span className={`font-semibold tabular-nums ${p.status === 'voided' ? muted : dark ? 'text-emerald-400' : 'text-emerald-700'}`}>{usd((p.amount_cents || 0) + (p.tip_cents || 0))}</span>
        </div>
      ))}
    </div>
  );
}
